import { useEffect, useState } from 'react';
import { Plus, Save, Trash2, X } from 'lucide-react';
import {
  CANONICAL_SUBSEGMENTS,
  SPECIALTIES,
  SPECIALTY_LABEL,
  LAUNCH_SPECIALISMS,
  CLINICAL_SPECIALISM_LABEL,
  ALL_MARKET_CODES,
  ALL_MARKET_NAMES,
} from '@hc/shared';
import {
  useBuyerMandates,
  useCreateBuyerMandate,
  useUpdateBuyerMandate,
  useDeleteBuyerMandate,
} from '../../api/hooks.js';
import type { BuyerMandateRow } from '../../api/client.js';
import { PanelFrame } from '../panel-frame.js';
import { MultiSelect, type MultiSelectOption } from '../multi-select.js';
import type { PanelId, TabId } from '../store.js';

// Mandate — the buyer's saved BD strategy: what they're building. The upstream
// context that makes Prescription (and the blueprint thesis) buyer-relative.
// Per-user, Clerk-isolated (never pooled). v1 = the strategy fields; the owned-
// portfolio ingest is a follow slice.

const APPETITE: ReadonlyArray<{ token: string; label: string }> = [
  { token: 'platform', label: 'Platform' },
  { token: 'bolt_on', label: 'Bolt-on' },
  { token: 'buy_and_build', label: 'Buy-and-build' },
  { token: 'carve_out', label: 'Carve-out' },
  { token: 'succession', label: 'Succession' },
  { token: 'turnaround', label: 'Turnaround' },
];

// Operating model — post-close, distinct from deal appetite (deal structure).
const INVOLVEMENT: ReadonlyArray<{ token: string; label: string }> = [
  { token: 'hands_off', label: 'Hands-off' },
  { token: 'operational_support', label: 'Operational support' },
  { token: 'turnaround', label: 'Turnaround' },
];
const MANAGEMENT: ReadonlyArray<{ token: string; label: string }> = [
  { token: 'retain', label: 'Retain incumbent' },
  { token: 'replace', label: 'Replace / install' },
];

const MARKET_OPTIONS = [...ALL_MARKET_CODES]
  .map((c) => ({ token: c, label: ALL_MARKET_NAMES[c] }))
  .sort((a, b) => a.label.localeCompare(b.label));
const SUBSEG_OPTIONS = CANONICAL_SUBSEGMENTS.map((s) => ({
  token: s as string,
  label: s as string,
}));
const CLINICAL_OPTIONS = LAUNCH_SPECIALISMS.map((s) => ({
  token: s.token,
  label: CLINICAL_SPECIALISM_LABEL[s.token] ?? s.token,
}));

interface Draft {
  name: string;
  countries: Set<string>;
  subsegments: Set<string>;
  specialties: Set<string>;
  clinicalSpecialisms: Set<string>;
  appetite: Set<string>;
  involvement: Set<string>;
  management: Set<string>;
  noGos: Set<string>;
  revMinM: string; // €m, string for the input
  revMaxM: string;
  notes: string;
}

function emptyDraft(): Draft {
  return {
    name: '',
    countries: new Set(),
    subsegments: new Set(),
    specialties: new Set(),
    clinicalSpecialisms: new Set(),
    appetite: new Set(),
    involvement: new Set(),
    management: new Set(),
    noGos: new Set(),
    revMinM: '',
    revMaxM: '',
    notes: '',
  };
}

function draftFromRow(r: BuyerMandateRow): Draft {
  return {
    name: r.name,
    countries: new Set(r.countries),
    subsegments: new Set(r.subsegments),
    specialties: new Set(r.specialties),
    clinicalSpecialisms: new Set(r.clinicalSpecialisms),
    appetite: new Set(r.appetite),
    involvement: new Set(r.involvement),
    management: new Set(r.management),
    noGos: new Set(r.noGos),
    revMinM: r.revMinEur != null ? String(r.revMinEur / 1_000_000) : '',
    revMaxM: r.revMaxEur != null ? String(r.revMaxEur / 1_000_000) : '',
    notes: r.notes ?? '',
  };
}

const EMPTY_SET: ReadonlySet<string> = new Set();

function ChipSelect({
  label,
  options,
  selected = EMPTY_SET,
  onToggle,
}: {
  label: string;
  options: ReadonlyArray<{ token: string; label: string }>;
  // Optional + defaulted so a stale draft (e.g. after a field is added) can never
  // crash the panel on `selected.size`.
  selected?: ReadonlySet<string>;
  onToggle: (token: string) => void;
}) {
  return (
    <div className="mnd-field">
      <div className="mnd-label">
        {label}
        {selected.size > 0 && <span className="mnd-count">{selected.size}</span>}
      </div>
      <div className="mnd-chips">
        {options.map((o) => (
          <button
            key={o.token}
            type="button"
            className={'mnd-chip' + (selected.has(o.token) ? ' on' : '')}
            onClick={() => onToggle(o.token)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const CHILDREN_OF = (parent: string) =>
  SPECIALTIES.filter((s) => s.parent === parent).map((s) => ({
    token: s.token,
    label: SPECIALTY_LABEL[s.token] ?? s.token,
  }));

// Sub-segment dropdown options — canonical-17 parents each followed by their
// fine specialties (indented). Parents write `subsegments`, children write
// `specialties`; the two stores are routed via a token prefix.
const SUBSEG_MS_OPTIONS: MultiSelectOption[] = CANONICAL_SUBSEGMENTS.flatMap((c) => [
  { token: `sub:${c}`, label: c as string },
  ...CHILDREN_OF(c).map((ch) => ({ token: `spec:${ch.token}`, label: ch.label, indent: true })),
]);

// Dropdown field — label, MultiSelect trigger, and the current selection as
// removable chips underneath (the mandate stays glanceable without opening
// every dropdown).
function DropdownField({
  label,
  placeholder,
  options,
  selected = EMPTY_SET,
  onToggle,
  onClear,
}: {
  label: string;
  placeholder: string;
  options: ReadonlyArray<MultiSelectOption>;
  selected?: ReadonlySet<string>;
  onToggle: (token: string) => void;
  onClear: () => void;
}) {
  const chips = options.filter((o) => selected.has(o.token));
  return (
    <div className="mnd-field">
      <div className="mnd-label">{label}</div>
      <div>
        <MultiSelect
          label={placeholder}
          options={options}
          selected={selected}
          onToggle={onToggle}
          onClear={onClear}
        />
      </div>
      {chips.length > 0 && (
        <div className="mnd-chips">
          {chips.map((o) => (
            <button
              key={o.token}
              type="button"
              className="mnd-chip on"
              title="Remove"
              onClick={() => onToggle(o.token)}
            >
              {o.label}
              <X size={10} strokeWidth={2.5} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function MandatePanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const { data } = useBuyerMandates();
  const mandates = data?.data ?? [];
  const create = useCreateBuyerMandate();
  const update = useUpdateBuyerMandate();
  const del = useDeleteBuyerMandate();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [error, setError] = useState<string | null>(null);

  // Load the selected mandate into the draft when the selection CHANGES. Keyed
  // on selectedId only (not `mandates`) on purpose: `mandates` changes on every
  // refetch, and re-running would wipe in-progress edits. The list is always
  // loaded before a selection can be made, so reading it from the closure is safe.
  useEffect(() => {
    if (selectedId === null) return;
    const row = mandates.find((m) => m.id === selectedId);
    if (row) setDraft(draftFromRow(row));
  }, [selectedId]);

  const toggle = (key: keyof Draft) => (token: string) =>
    setDraft((d) => {
      const next = new Set(d[key] as Set<string>);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return { ...d, [key]: next };
    });

  const clear =
    (...keys: Array<keyof Draft>) =>
    () =>
      setDraft((d) => ({
        ...d,
        ...Object.fromEntries(keys.map((k) => [k, new Set<string>()])),
      }));

  // Sub-segments + specialties live in one dropdown; tokens are prefixed and
  // routed back to their own draft fields.
  const subsegSelected = new Set([
    ...[...draft.subsegments].map((s) => `sub:${s}`),
    ...[...draft.specialties].map((s) => `spec:${s}`),
  ]);
  const toggleSubsegToken = (token: string) =>
    token.startsWith('sub:')
      ? toggle('subsegments')(token.slice(4))
      : toggle('specialties')(token.slice(5));

  const startNew = () => {
    setSelectedId(null);
    setDraft(emptyDraft());
    setError(null);
  };

  const toInput = () => ({
    name: draft.name.trim(),
    countries: [...draft.countries],
    subsegments: [...draft.subsegments],
    specialties: [...draft.specialties],
    clinicalSpecialisms: [...draft.clinicalSpecialisms],
    regions: [] as string[],
    appetite: [...draft.appetite],
    involvement: [...draft.involvement],
    management: [...draft.management],
    revMinEur: draft.revMinM ? Math.round(Number(draft.revMinM) * 1_000_000) : null,
    revMaxEur: draft.revMaxM ? Math.round(Number(draft.revMaxM) * 1_000_000) : null,
    noGos: [...draft.noGos],
    notes: draft.notes.trim() || null,
  });

  const save = async () => {
    setError(null);
    if (!draft.name.trim()) {
      setError('Give the mandate a name.');
      return;
    }
    try {
      if (selectedId) {
        await update.mutateAsync({ id: selectedId, ...toInput() });
      } else {
        const res = await create.mutateAsync(toInput());
        setSelectedId(res.data.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  };

  const remove = async () => {
    if (!selectedId) return;
    try {
      await del.mutateAsync(selectedId);
      startNew();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    }
  };

  const busy = create.isPending || update.isPending || del.isPending;

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="mnd-root">
        <style>{MND_CSS}</style>

        <div className="mnd-top">
          <select
            className="mnd-picker"
            value={selectedId ?? '__new__'}
            onChange={(e) =>
              e.target.value === '__new__' ? startNew() : setSelectedId(e.target.value)
            }
          >
            <option value="__new__">+ New mandate</option>
            {mandates.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <button type="button" className="mnd-btn" onClick={startNew} title="New mandate">
            <Plus size={13} />
          </button>
          <div style={{ flex: 1 }} />
          {selectedId && (
            <button type="button" className="mnd-btn danger" onClick={remove} disabled={busy}>
              <Trash2 size={13} /> Delete
            </button>
          )}
          <button type="button" className="mnd-btn primary" onClick={save} disabled={busy}>
            <Save size={13} /> {busy ? 'Saving…' : selectedId ? 'Save' : 'Create'}
          </button>
        </div>

        {error && <div className="mnd-error">{error}</div>}

        <div className="mnd-body">
          <p className="mnd-intro">
            Your acquisition strategy — what you’re building. It shapes Prescription and the
            blueprint thesis so recommendations are relative to <em>you</em>. Private to your
            account.
          </p>

          <div className="mnd-field">
            <div className="mnd-label">Mandate name</div>
            <input
              className="mnd-input"
              value={draft.name}
              placeholder="e.g. UK dementia-care roll-up"
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </div>

          <DropdownField
            label="Target markets"
            placeholder="Add markets…"
            options={MARKET_OPTIONS}
            selected={draft.countries}
            onToggle={toggle('countries')}
            onClear={clear('countries')}
          />
          <DropdownField
            label="Sub-segments"
            placeholder="Add sub-segments…"
            options={SUBSEG_MS_OPTIONS}
            selected={subsegSelected}
            onToggle={toggleSubsegToken}
            onClear={clear('subsegments', 'specialties')}
          />
          <DropdownField
            label="Clinical specialisms"
            placeholder="Add specialisms…"
            options={CLINICAL_OPTIONS}
            selected={draft.clinicalSpecialisms}
            onToggle={toggle('clinicalSpecialisms')}
            onClear={clear('clinicalSpecialisms')}
          />
          <ChipSelect
            label="Deal appetite"
            options={APPETITE}
            selected={draft.appetite}
            onToggle={toggle('appetite')}
          />
          <ChipSelect
            label="Involvement"
            options={INVOLVEMENT}
            selected={draft.involvement}
            onToggle={toggle('involvement')}
          />
          <ChipSelect
            label="Management"
            options={MANAGEMENT}
            selected={draft.management}
            onToggle={toggle('management')}
          />

          <div className="mnd-field">
            <div className="mnd-label">Deal size band (€m per target)</div>
            <div className="mnd-band">
              <input
                className="mnd-input sm"
                type="number"
                min={0}
                value={draft.revMinM}
                placeholder="min"
                onChange={(e) => setDraft((d) => ({ ...d, revMinM: e.target.value }))}
              />
              <span>–</span>
              <input
                className="mnd-input sm"
                type="number"
                min={0}
                value={draft.revMaxM}
                placeholder="max"
                onChange={(e) => setDraft((d) => ({ ...d, revMaxM: e.target.value }))}
              />
            </div>
          </div>

          <DropdownField
            label="No-gos (sub-segments to exclude)"
            placeholder="Add exclusions…"
            options={SUBSEG_OPTIONS}
            selected={draft.noGos}
            onToggle={toggle('noGos')}
            onClear={clear('noGos')}
          />

          <div className="mnd-field">
            <div className="mnd-label">Thesis / gaps (notes)</div>
            <textarea
              className="mnd-input area"
              value={draft.notes}
              rows={3}
              placeholder="Where you want to grow, capability gaps to fill, angle…"
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            />
          </div>
        </div>
      </div>
    </PanelFrame>
  );
}

const MND_CSS = `
.mnd-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); }
.mnd-top { display:flex; align-items:center; gap:var(--s-2); padding:var(--s-2) var(--s-3); border-bottom:1px solid var(--hairline); background:var(--surface); }
.mnd-picker { font:inherit; font-size:var(--t-body); padding:2px var(--s-2); border:1px solid var(--hairline); border-radius:2px; background:var(--surface); color:var(--text-primary); max-width:220px; }
.mnd-btn { display:inline-flex; align-items:center; gap:5px; font:inherit; font-size:var(--t-body); padding:3px var(--s-2); border:1px solid var(--hairline); border-radius:2px; background:var(--surface); color:var(--text-primary); cursor:pointer; }
.mnd-btn:hover:not(:disabled) { background:var(--hover-subtle); }
.mnd-btn:disabled { opacity:0.5; cursor:default; }
.mnd-btn.primary { background:var(--accent); border-color:var(--accent); color:var(--bg); font-weight:600; }
.mnd-btn.danger { color:var(--negative,#c0143c); border-color:color-mix(in srgb, var(--negative,#c0143c) 40%, transparent); }
.mnd-error { padding:6px var(--s-3); background:color-mix(in srgb, var(--negative,#c0143c) 12%, transparent); color:var(--negative,#c0143c); font-size:var(--t-meta); }
.mnd-body { flex:1; overflow:auto; padding:var(--s-3); min-height:0; max-width:720px; display:flex; flex-direction:column; gap:var(--s-3); }
.mnd-intro { margin:0; line-height:1.5; color:var(--text-tertiary); font-size:var(--t-meta); }
.mnd-intro em { color:var(--text-secondary); font-style:italic; }
.mnd-field { display:flex; flex-direction:column; gap:5px; }
.mnd-label { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.05em; color:var(--text-secondary); font-weight:700; display:flex; align-items:center; gap:6px; }
.mnd-count { background:var(--accent); color:var(--bg); border-radius:8px; padding:0 6px; font-size:9px; font-weight:700; }
.mnd-chips { display:flex; flex-wrap:wrap; gap:5px; }
.mnd-chip { display:inline-flex; align-items:center; gap:4px; font:inherit; font-size:var(--t-meta); padding:2px 8px; border:1px solid var(--hairline); border-radius:12px; background:var(--surface); color:var(--text-secondary); cursor:pointer; }
.mnd-chip:hover { background:var(--hover-subtle); }
.mnd-chip.on { background:var(--color-accent-100); border-color:var(--accent); color:var(--text-primary); font-weight:600; }
.mnd-input { font:inherit; font-size:var(--t-body); padding:4px var(--s-2); border:1px solid var(--hairline); border-radius:2px; background:var(--surface); color:var(--text-primary); width:100%; box-sizing:border-box; }
.mnd-input.sm { width:100px; }
.mnd-input.area { resize:vertical; line-height:1.5; }
.mnd-band { display:flex; align-items:center; gap:8px; color:var(--text-tertiary); }
`;
